import SEO from "@/components/SEO";
import FAQAccordion from "@/components/FAQAccordion";
import { RelatedLinks } from "@/pages/ServicePage";
import { PageHero, SectionHead, CTABand, Reveal } from "@/components/Ui";
import { CheckCircle2 } from "lucide-react";

const prestations = [
  "Maçonnerie générale : murs porteurs, murets, façades",
  "Fondations et semelles adaptées à votre terrain",
  "Dalles et chapes béton, intérieures et extérieures",
  "Murs en parpaings, briques ou biobrique",
  "Ouvrages en béton : poteaux, linteaux, poutres",
  "Maçonnerie traditionnelle : chaux, brique, pierre, silex",
  "Travaux de gros œuvre pour constructions neuves",
  "Réparations de maçonnerie et reprises ponctuelles",
];

const faq = [
  { q: "Que comprend la maçonnerie générale ?", a: "Tous les travaux de structure et d'enveloppe : murs, fondations, dalles, murets, ouvrages en béton, ainsi que la maçonnerie traditionnelle et les réparations." },
  { q: "Intervenez-vous pour les professionnels ?", a: "Oui, nous travaillons pour les particuliers comme pour les professionnels, à Bernay, Évreux et dans toute l'Eure." },
  { q: "Comment obtenir un devis de maçonnerie ?", a: "Appelez-nous au 07 45 04 91 89 ou remplissez le formulaire : nous étudions votre projet et vous remettons un devis gratuit et détaillé." },
];

export default function MaconnerieBernay() {
  return (
    <>
      <SEO
        title="Maçonnerie à Bernay | Entreprise de maçonnerie Eure – DMZ Normandie"
        description="DMZ Normandie, entreprise de maçonnerie à Bernay et Évreux : murs, parpaings, briques, fondations, dalles béton, gros œuvre. Devis gratuit — 07 45 04 91 89."
        path="/maconnerie-bernay"
      />
      <PageHero
        title="Maçonnerie à Bernay"
        subtitle="Vous cherchez un maçon à Bernay ? DMZ Normandie réalise vos travaux de maçonnerie générale à Bernay, Évreux et dans l'Eure : construction de murs, fondations, dalles béton et gros œuvre, pour les particuliers comme pour les professionnels."
      />

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal>
            <SectionHead overline="Nos prestations" title="Une maçonnerie complète, du sol au toit" />
            <div className="space-y-4 -mt-6">
              {prestations.map((p) => (
                <p key={p} className="flex items-start gap-3 text-zinc-300 font-light">
                  <CheckCircle2 size={18} className="text-[#C4A484] mt-0.5 shrink-0" /> {p}
                </p>
              ))}
            </div>
            <div className="mt-10">
              <a href="/contact" className="btn-sable" data-testid="cta-devis-maconnerie">Demander un devis de maçonnerie</a>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="space-y-6">
            <div className="overflow-hidden relative">
              <img src="/realisations/maison-contemporaine.webp" alt="Entreprise de maçonnerie à Bernay : maison construite par DMZ Normandie" className="w-full h-72 object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-black/25" />
            </div>
            <div className="card-dark p-8">
              <h2 className="font-serif-display font-light text-2xl md:text-3xl text-[#F5F5F0] mb-4">Un maçon de proximité à Bernay</h2>
              <p className="text-sm font-light text-zinc-400 leading-relaxed">
                DMZ Normandie se déplace gratuitement pour étudier votre projet, vous conseiller sur les
                matériaux (parpaing, brique, biobrique ou béton) et vous remettre un devis clair et détaillé.
              </p>
              <p className="mt-4 text-sm font-light text-zinc-400 leading-relaxed">
                Chaque chantier est préparé avec soin : étude du terrain, choix des fondations,
                mise en œuvre dans les règles de l'art et finitions propres.
              </p>
              <p className="mt-4 text-sm font-light text-zinc-400 leading-relaxed">
                Basée près de Bernay, notre entreprise de maçonnerie intervient aussi à Évreux et dans
                toute l'Eure. Selon la nature et l'importance du chantier, nous nous déplaçons dans
                d'autres secteurs de Normandie. Chaque projet commence par une visite gratuite :
                nous écoutons vos besoins, examinons le bâti ou le terrain, puis nous vous remettons
                un chiffrage détaillé poste par poste.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="max-w-4xl mx-auto px-6 lg:px-10 mt-20">
          <SectionHead overline="Questions fréquentes" title="Vos questions, nos réponses" />
          <FAQAccordion items={faq} testid="faq-maconnerie" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-16">
          <Reveal>
            <p className="text-[10px] tracking-[0.5em] uppercase text-[#C4A484] mb-5">Voir aussi</p>
            <RelatedLinks slugs={["renovation-bernay", "fondations", "construction-murs", "extension-maison"]} />
          </Reveal>
        </div>
      </section>

      <CTABand />
    </>
  );
}
